import {
  Activity,
  Compass,
  ChartNoAxesCombined,
  Database,
  LayoutDashboard,
  PanelLeft,
  Settings,
  SlidersHorizontal,
  type LucideIcon,
} from 'lucide-react';
import { NavLink } from 'react-router-dom';
import { Logo } from '@/components/logo';
import { UserMenu } from '@/components/user-menu';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type SidebarLink = {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
};

const SidebarNavLink = ({
  item,
  collapsed,
  onNavigate,
}: {
  item: SidebarLink;
  collapsed: boolean;
  onNavigate?: () => void;
}) => {
  const Icon = item.icon;

  return (
    <NavLink
      to={item.to}
      end={item.end}
      title={collapsed ? item.label : undefined}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex h-10 items-center rounded-lg text-sm font-medium transition-colors',
          collapsed ? 'justify-center px-0' : 'gap-3 px-3',
          isActive
            ? 'bg-surface text-ink shadow-sm'
            : 'text-ink-muted hover:bg-surface-muted hover:text-ink',
        )
      }
    >
      <Icon className="size-4 shrink-0" />
      <span className={cn('truncate', collapsed && 'sr-only')}>
        {item.label}
      </span>
    </NavLink>
  );
};

export const AppSidebar = ({
  currentProjectId,
  collapsed = false,
  mobile = false,
  onToggle,
  onNavigate,
}: {
  currentProjectId: string | undefined;
  collapsed?: boolean;
  mobile?: boolean;
  onToggle?: () => void;
  onNavigate?: () => void;
}) => {
  const isCollapsed = collapsed && !mobile;
  const projectBase = currentProjectId
    ? `/projects/${encodeURIComponent(currentProjectId)}`
    : null;
  const projectLinks: SidebarLink[] = projectBase
    ? [
        {
          to: projectBase,
          label: 'Opportunities',
          icon: Compass,
          end: true,
        },
        {
          to: `${projectBase}/overview`,
          label: 'Overview',
          icon: LayoutDashboard,
          end: true,
        },
        {
          to: `${projectBase}/reports`,
          label: 'Reports',
          icon: ChartNoAxesCombined,
          end: true,
        },
        {
          to: `${projectBase}/realtime`,
          label: 'Realtime',
          icon: Activity,
          end: true,
        },
      ]
    : [];

  return (
    <aside
      className={cn(
        'flex h-full flex-col border-r border-border bg-surface-muted/45',
        !mobile &&
          'transition-[width] duration-200 ease-out motion-reduce:transition-none',
        mobile ? 'w-full' : isCollapsed ? 'w-14' : 'w-60',
      )}
    >
      <div
        className={cn(
          'flex h-14 shrink-0 items-center border-b border-border',
          isCollapsed ? 'justify-center px-2' : 'justify-between px-4',
        )}
      >
        <Logo showWordmark={!isCollapsed} />
        {!mobile && !isCollapsed ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-8"
            onClick={onToggle}
            aria-label="Collapse sidebar"
          >
            <PanelLeft />
          </Button>
        ) : null}
      </div>

      <nav className="flex min-h-0 flex-1 flex-col overflow-y-auto p-2">
        {projectLinks.length > 0 ? (
          <div>
            {!isCollapsed ? (
              <p className="mb-1 px-3 text-xs font-medium text-ink-subtle">
                Insights
              </p>
            ) : (
              <div className="mx-2 mb-2 h-px bg-border" />
            )}
            <div className="space-y-1">
              {projectLinks.map((item) => (
                <SidebarNavLink
                  key={item.to}
                  item={item}
                  collapsed={isCollapsed}
                  onNavigate={onNavigate}
                />
              ))}
            </div>

            {!isCollapsed ? (
              <p className="mb-1 mt-5 px-3 text-xs font-medium text-ink-subtle">
                Project
              </p>
            ) : (
              <div className="mx-2 mb-2 mt-5 h-px bg-border" />
            )}
            <div className="space-y-1">
              <SidebarNavLink
                item={{
                  to: `${projectBase}/data-sources`,
                  label: 'Data sources',
                  icon: Database,
                  end: true,
                }}
                collapsed={isCollapsed}
                onNavigate={onNavigate}
              />
              <SidebarNavLink
                item={{
                  to: `${projectBase}/details`,
                  label: 'Project details',
                  icon: SlidersHorizontal,
                  end: true,
                }}
                collapsed={isCollapsed}
                onNavigate={onNavigate}
              />
            </div>
          </div>
        ) : null}
      </nav>

      <div className="space-y-1 border-t border-border p-2">
        <SidebarNavLink
          item={{
            to: '/account',
            label: 'Account settings',
            icon: Settings,
            end: true,
          }}
          collapsed={isCollapsed}
          onNavigate={onNavigate}
        />

        <UserMenu sidebar collapsed={isCollapsed} mobile={mobile} />

        {!mobile && isCollapsed ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-10 w-full"
            onClick={onToggle}
            aria-label="Expand sidebar"
            title="Expand sidebar"
          >
            <PanelLeft />
          </Button>
        ) : null}
      </div>
    </aside>
  );
};
